import { defineComponent as _defineComponent } from 'vue'
import {
  createUnsupportedClientToolResult,
  executeAssistantRun,
  fetchGetTools,
  isAssistantChatConfigured,
  listAssistantConversations,
  listAssistantMessages,
} from '@/apis/assistant'
import type {
  ClientToolExecution,
  HarnessPendingToolCall,
} from '@/apis/assistant'
import router from '@/router'
import { Message, MessageBox } from 'element-ui'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import {
  applyAssistantConversationMessages,
  createAssistantConversationSession,
  createAssistantSession,
  findOrCreateAssistantSession,
} from './assistant-conversation-mapper'
import type {
  AssistantMessage,
  AssistantSession,
  OrderImportCardState,
  ProductionHistoryCardState,
} from './assistant-models'
import { downloadSelectedProductionFiles } from './assistant-production-download'
import {
  appendAssistantDelta,
  applyAssistantDone,
  applyAssistantQueued,
  applyAssistantRequiresAction,
  applyAssistantStart,
  beginAssistantTurn,
  failAssistantRun,
  failAssistantToolRun,
} from './assistant-session-state'
import { ASSISTANT_SYSTEM_PROMPT } from './assistant-system-prompt'
import { applyClientToolResult } from './assistant-tool-renderers'
import { getClientToolDefinition, runClientTool } from './assistant-tool-runner'
import { createAssistantToolsLoader } from './assistant-tools-loader'
import { getOrderImportRoute } from './assistant-ui-actions'
import { ASSISTANT_PANEL_Z_INDEX } from './constants'
import GAssistantComposer from './g-assistant-composer.vue'
import GAssistantTimeline from './g-assistant-timeline.vue'
import { createOrderImportCardState } from './order-import-state'
import {
  createProductionHistoryCardState,
  exportProductionHistorySelections as exportProductionHistoryState,
} from './production-history-state'


export default /*#__PURE__*/_defineComponent({
  __name: 'g-assistant-panel',
  props: { visible: { type: Boolean, default: false } },
  emits: ['close'],
  setup(__props, { emit: emits }) {

const props = __props



const sessions = ref<AssistantSession[]>([createAssistantSession('current')])
const activeSessionId = ref(sessions.value[0].id)
const activeGroup = ref<'今天' | '昨天' | '近一周'>('今天')
const historyQuery = ref('')
const historyCollapsed = ref(true)
const timelineRef = ref<{ scrollToBottom: () => void } | null>(null)
const composerRef = ref<{ reset: () => void } | null>(null)
const groups = ['今天', '昨天', '近一周'] as const
const assistantToolsLoader = createAssistantToolsLoader(fetchGetTools)
const activeSession = computed(
  () =>
    sessions.value.find((item) => item.id === activeSessionId.value) ||
    sessions.value[0]
)
const conversationListError = ref('')
let conversationListLoaded = false
const busy = computed(() =>
  ['running', 'queued'].includes(activeSession.value.status)
)
const filteredSessions = computed(() =>
  sessions.value.filter(
    (item) =>
      item.group === activeGroup.value &&
      item.title.includes(historyQuery.value.trim())
  )
)
function scrollToBottom() {
  timelineRef.value?.scrollToBottom()
}
function newSession() {
  const { session, created } = findOrCreateAssistantSession(sessions.value)
  if (created) sessions.value.unshift(session)
  activeSessionId.value = session.id
  activeGroup.value = session.group
  scrollToBottom()
}

function preloadAssistantTools() {
  if (!isAssistantChatConfigured()) return
  void assistantToolsLoader.load().catch(() => undefined)
}

async function loadConversationList() {
  if (!isAssistantChatConfigured() || conversationListLoaded) return

  try {
    const page = await listAssistantConversations({
      santiType: 'gg_ai_assistant',
      limit: 50,
    })
    const newSession =
      sessions.value.find((item) => !item.conversationId) ||
      createAssistantSession()
    const existingByConversationId = new Map(
      sessions.value
        .filter((item) => item.conversationId)
        .map((item) => [item.conversationId as string, item])
    )
    sessions.value = [
      newSession,
      ...page.items.map((conversation) => {
        const summary = createAssistantConversationSession(conversation)
        const existing = existingByConversationId.get(
          conversation.conversationId
        )
        return existing ? { ...summary, messages: existing.messages } : summary
      }),
    ]
    activeSessionId.value = newSession.id
    activeGroup.value = newSession.group
    conversationListLoaded = true
    conversationListError.value = ''
  } catch {
    conversationListError.value = '历史会话加载失败'
  }
}

async function selectSession(session: AssistantSession) {
  activeSessionId.value = session.id
  historyCollapsed.value = true
  activeGroup.value = session.group
  if (!session.conversationId || session.messages.length) {
    scrollToBottom()
    return
  }

  try {
    const page = await listAssistantMessages(session.conversationId, {
      includeTools: true,
      limit: 50,
    })
    commitSession(applyAssistantConversationMessages(session, page.items))
    scrollToBottom()
  } catch {
    conversationListError.value = '会话消息加载失败'
  }
}
function confirmDeleteSession(session: AssistantSession) {
  if (sessions.value.length === 1) return
  MessageBox.confirm(`确认删除“${session.title}”吗？`, '删除会话', {
    confirmButtonText: '删除',
    cancelButtonText: '取消',
    type: 'warning',
  })
    .then(() => deleteSession(session.id))
    .catch(() => undefined)
}

function deleteSession(id: string) {
  if (sessions.value.length === 1) return
  sessions.value = sessions.value.filter((item) => item.id !== id)
  if (activeSessionId.value === id) activeSessionId.value = sessions.value[0].id
}
function commitSession(session: AssistantSession): AssistantSession {
  const index = sessions.value.findIndex((item) => item.id === session.id)
  if (index === -1) return session

  sessions.value.splice(index, 1, session)
  return session
}

async function send(raw: string) {
  const text = raw.trim()
  if (!text || busy.value) return
  composerRef.value?.reset()

  const session = activeSession.value
  const typingMessageId = `typing-${Date.now()}`
  const streamMessageId = `assistant-${Date.now()}`
  const actionMessageId = `action-${Date.now()}`
  const errorMessageId = `failure-${Date.now()}`

  session.messages.push({
    id: `user-${Date.now()}`,
    role: 'user',
    kind: 'text',
    time: new Date().toTimeString().slice(0, 5),
    text,
  })
  session.title = text.slice(0, 16)
  scrollToBottom()

  let current = commitSession(beginAssistantTurn(session, typingMessageId))
  scrollToBottom()

  if (!isAssistantChatConfigured()) {
    commitSession(
      failAssistantRun(current, {
        typingMessageId,
        messageId: errorMessageId,
        message: '助手服务未配置，请联系管理员。',
      })
    )
    scrollToBottom()
    return
  }

  const controller = new AbortController()
  let hasClientTool = false
  const submitClientToolResult = async (
    execution: ClientToolExecution,
    runId: string,
    cardMessageId?: string,
    emptyReplyMessage?: string,
    followUpTypingMessageId = ''
  ) => {
    const followUpMessageId = `tool-follow-up-${execution.toolCallId}`
    let hasFollowUpContent = false
    await executeAssistantRun(
      {
        conversationId: current.conversationId || '',
        runId,
        toolCallId: execution.toolCallId,
        content: execution.resultForAgent,
      },
      controller.signal,
      (event) => {
        if (event.type === 'message_delta') {
          if (cardMessageId) return
          hasFollowUpContent = true
          current = commitSession(
            appendAssistantDelta(current, {
              typingMessageId: followUpTypingMessageId,
              messageId: followUpMessageId,
              content: event.content,
            })
          )
        } else if (event.type === 'done') {
          if (cardMessageId) {
            current = commitSession({
              ...current,
              runId: event.run.runId,
              status: event.run.status === 'completed' ? 'completed' : 'failed',
            })
          } else {
            current = commitSession(
              applyAssistantDone(current, {
                typingMessageId: followUpTypingMessageId,
                runId: event.run.runId,
                status: event.run.status,
                messageId: followUpMessageId,
                finalReply:
                  event.run.finalReply ||
                  (hasFollowUpContent ? null : emptyReplyMessage),
              })
            )
          }
        } else if (event.type === 'queued' || event.type === 'accepted') {
          current = commitSession(
            applyAssistantQueued(current, {
              conversationId: event.conversationId,
              runId: event.runId,
              queueSequence: event.queueSequence,
              queuePosition: event.queuePosition,
              typingMessageId: followUpTypingMessageId,
              messageId: followUpMessageId,
            })
          )
        } else if (event.type === 'requires_action') {
          current = commitSession(
            applyAssistantRequiresAction(current, {
              typingMessageId: followUpTypingMessageId,
              runId: event.run.runId,
              messageId: followUpMessageId,
            })
          )
        } else if (event.type === 'error') {
          current = commitSession(
            failAssistantRun(current, {
              typingMessageId: followUpTypingMessageId,
              messageId: followUpMessageId,
              message: event.message,
            })
          )
        }
      }
    )
  }
  const executeProductionRecordsTool = async (
    toolCall: HarnessPendingToolCall,
    runId: string
  ) => {
    const execution: ClientToolExecution = await runClientTool(toolCall)
    current = commitSession({
      ...current,
      messages: current.messages.map((message) =>
        message.toolCallId === toolCall.toolCallId
          ? {
              ...applyClientToolResult(message, execution),
              toolStatus: 'completed',
            }
          : message
      ),
    })
    await submitClientToolResult(
      execution,
      runId,
      `production-${toolCall.toolCallId}`
    )
  }
  const executeOrderListTool = async (
    toolCall: HarnessPendingToolCall,
    runId: string
  ) => {
    const execution = await runClientTool(toolCall)
    current = commitSession({
      ...current,
      messages: current.messages.map((message) =>
        message.toolCallId === toolCall.toolCallId
          ? execution.orderData?.orders.length
            ? {
                ...message,
                toolStatus: 'completed',
                orderImportOrders: execution.orderData.orders,
                orderImportSummary: execution.orderData,
                orderImport: createOrderImportCardState(
                  execution.orderData.orders
                ),
              }
            : {
                ...message,
                toolStatus: 'completed',
                kind: 'text',
                text: '当前查询条件没有可导入的订单',
                orderImport: undefined,
              }
          : message
      ),
    })
    await submitClientToolResult(
      execution,
      runId,
      `order-import-${toolCall.toolCallId}`
    )
  }
  try {
    const tools = await assistantToolsLoader.load()
    await executeAssistantRun(
      {
        content: text,
        conversationId: session.conversationId,
        tools,
        // window.ASSISTANT_SYSTEM_PROMPT 是测试
        systemPrompt:
          window?.ASSISTANT_SYSTEM_PROMPT || ASSISTANT_SYSTEM_PROMPT,
      },
      controller.signal,
      (event) => {
        if (event.type === 'queued' || event.type === 'accepted') {
          current = commitSession(
            applyAssistantQueued(current, {
              conversationId: event.conversationId,
              runId: event.runId,
              queueSequence: event.queueSequence,
              queuePosition: event.queuePosition,
              typingMessageId,
              messageId: actionMessageId,
            })
          )
        } else if (event.type === 'start') {
          current = commitSession(
            applyAssistantStart(current, {
              conversationId: event.conversationId,
              runId: event.runId,
            })
          )
        } else if (event.type === 'message_delta') {
          current = commitSession(
            appendAssistantDelta(current, {
              typingMessageId,
              messageId: streamMessageId,
              content: event.content,
            })
          )
        } else if (event.type === 'requires_action') {
          const toolCall = event.run.pendingToolCalls[0]
          const toolDefinition = toolCall
            ? getClientToolDefinition(toolCall.name)
            : undefined
          if (toolCall && toolDefinition?.kind === 'production-history') {
            hasClientTool = true
            current = commitSession({
              ...current,
              runId: event.run.runId,
              status: 'running',
              messages: [
                ...current.messages.filter(
                  (message) =>
                    message.id !== typingMessageId &&
                    message.id !== streamMessageId
                ),
                {
                  id: `${toolDefinition.messageIdPrefix}-${toolCall.toolCallId}`,
                  role: 'assistant',
                  kind: 'production-history',
                  time: new Date().toTimeString().slice(0, 5),
                  toolCallId: toolCall.toolCallId,
                  toolName: toolCall.name,
                  toolStatus: 'running',
                  productionHistory: createProductionHistoryCardState(),
                  productionRecords: [],
                  productionQuery: toolCall.arguments,
                },
              ],
            })
            void executeProductionRecordsTool(toolCall, event.run.runId).catch(
              () => {
                current = commitSession(
                  failAssistantToolRun(current, {
                    typingMessageId,
                    toolCallId: toolCall.toolCallId,
                    messageId: errorMessageId,
                    message: toolDefinition.failureMessage,
                  })
                )
              }
            )
          } else if (toolCall && toolDefinition?.kind === 'order-import') {
            hasClientTool = true
            current = commitSession({
              ...current,
              runId: event.run.runId,
              status: 'running',
              messages: [
                ...current.messages.filter(
                  (message) =>
                    message.id !== typingMessageId &&
                    message.id !== streamMessageId
                ),
                {
                  id: `${toolDefinition.messageIdPrefix}-${toolCall.toolCallId}`,
                  role: 'assistant',
                  kind: 'order-import',
                  time: new Date().toTimeString().slice(0, 5),
                  toolCallId: toolCall.toolCallId,
                  toolName: toolCall.name,
                  orderImport: createOrderImportCardState(),
                  orderImportQuery: toolCall.arguments,
                  toolStatus: 'running',
                },
              ],
            })
            void executeOrderListTool(toolCall, event.run.runId).catch(() => {
              current = commitSession(
                failAssistantToolRun(current, {
                  typingMessageId,
                  toolCallId: toolCall.toolCallId,
                  messageId: errorMessageId,
                  message: toolDefinition.failureMessage,
                })
              )
            })
          } else if (
            toolCall &&
            toolCall.kind === 'client_tool' &&
            !toolCall.requiresConfirmation
          ) {
            hasClientTool = true
            current = commitSession({
              ...current,
              runId: event.run.runId,
              status: 'running',
              messages: [
                ...current.messages.filter(
                  (message) => message.id !== typingMessageId
                ),
                {
                  id: `tool-follow-up-typing-${toolCall.toolCallId}`,
                  role: 'assistant',
                  kind: 'typing',
                  time: new Date().toTimeString().slice(0, 5),
                },
              ],
            })
            void submitClientToolResult(
              createUnsupportedClientToolResult(toolCall),
              event.run.runId,
              undefined,
              '暂时无法处理该请求，请换一种问法。',
              `tool-follow-up-typing-${toolCall.toolCallId}`
            ).catch(() => {
              current = commitSession(
                failAssistantRun(current, {
                  typingMessageId: `tool-follow-up-typing-${toolCall.toolCallId}`,
                  messageId: errorMessageId,
                  message: `Tool 执行失败：${toolCall.name}`,
                })
              )
            })
          } else {
            current = commitSession(
              applyAssistantRequiresAction(current, {
                typingMessageId,
                runId: event.run.runId,
                messageId: actionMessageId,
              })
            )
          }
        } else if (event.type === 'done') {
          if (!hasClientTool) {
            current = commitSession(
              applyAssistantDone(current, {
                typingMessageId,
                runId: event.run.runId,
                status: event.run.status,
                messageId: streamMessageId,
                finalReply: event.run.finalReply,
              })
            )
          }
        } else if (event.type === 'error') {
          current = commitSession(
            failAssistantRun(current, {
              typingMessageId,
              messageId: errorMessageId,
              message: event.message,
            })
          )
        }
        // console.log('event', event)
        scrollToBottom()
      }
    )
  } catch {
    if (!controller.signal.aborted) {
      commitSession(
        failAssistantRun(current, {
          typingMessageId,
          messageId: errorMessageId,
          message: '本地助手服务请求失败',
        })
      )
      scrollToBottom()
    }
  }
}
function updateOrderImportState(
  message: AssistantMessage,
  state: OrderImportCardState
) {
  if (message.orderImport) message.orderImport = state
}
function importOrderSelections(message: AssistantMessage) {
  if (!message.orderImport?.selectedCabinetIds.length) return

  if (message.orderImportOrders) {
    const selectedRoomIds = message.orderImport.selectedCabinetIds
    const selectedOrders = message.orderImportOrders.filter((order) =>
      order.rooms.some((room) => selectedRoomIds.includes(room.id))
    )
    router.push({
      path: '/materialList',
      query: {
        assistant_room_ids: selectedRoomIds.join(','),
        assistant_order_ids: selectedOrders.map((order) => order.id).join(','),
        assistant_order_codes: selectedOrders
          .map((order) => order.orderNo)
          .join(','),
      },
    })
    return
  }
  if (message.orderImport.dataId) {
    router.push(getOrderImportRoute(message.orderImport.dataId))
  }
}
function updateProductionHistoryState(
  message: AssistantMessage,
  state: ProductionHistoryCardState
) {
  if (message.productionHistory) message.productionHistory = state
}
async function exportProductionHistorySelections(message: AssistantMessage) {
  if (!message.productionHistory || !message.productionRecords) return
  const selected = message.productionRecords.filter((record) =>
    message.productionHistory?.selectedRecordIds.includes(record.id)
  )
  const result = await downloadSelectedProductionFiles(selected)
  if (!result.downloaded) {
    Message.warning('选中的生产文件暂无下载地址')
    return
  }
  if (result.failed.length) {
    Message.warning(`${result.failed.length} 个生产文件下载失败，请重试`)
    return
  }
  message.productionHistory = exportProductionHistoryState(
    message.productionHistory
  )
  Message.success('生产文件已打包下载')
}

async function changeProductionHistoryPage(
  message: AssistantMessage,
  page: number
) {
  const toolArguments = message.productionQuery
  const query = toolArguments?.query as Record<string, unknown> | undefined
  if (!toolArguments || !query || message.productionLoading) return

  message.productionLoading = true
  try {
    const execution = await runClientTool({
      toolCallId: message.id,
      kind: 'function',
      name: message.toolName || 'get_material_export_records',
      arguments: {
        ...toolArguments,
        query: { ...query, page },
      },
      batchId: null,
      requiresConfirmation: false,
      parameterFingerprint: null,
    })
    Object.assign(message, applyClientToolResult(message, execution), {
      productionQuery: {
        ...toolArguments,
        query: { ...query, page },
      },
      productionHistory: createProductionHistoryCardState(),
    })
  } catch {
    Message.error('生产文件记录查询失败')
  } finally {
    message.productionLoading = false
  }
}
function handleClose() {
  emits('close')
}
function handleKeydown(event: KeyboardEvent) {
  if (props.visible && event.key === 'Escape') handleClose()
}
async function changeOrderImportPage(message: AssistantMessage, page: number) {
  const arguments_ = message.orderImportQuery
  const query = arguments_?.query as Record<string, unknown> | undefined
  if (!arguments_ || !query) return
  const execution = await runClientTool({
    toolCallId: message.id,
    kind: 'function',
    name: message.toolName || 'get_order_list',
    arguments: { ...arguments_, query: { ...query, page } },
    batchId: null,
    requiresConfirmation: false,
    parameterFingerprint: null,
  })
  if (!execution.orderData) return
  message.orderImportOrders = execution.orderData.orders
  message.orderImportSummary = execution.orderData
  message.orderImportQuery = { ...arguments_, query: { ...query, page } }
  message.orderImport = createOrderImportCardState(execution.orderData.orders)
}

watch(
  () => props.visible,
  (visible) => {
    if (!visible) {
      historyCollapsed.value = true
      return
    }
    scrollToBottom()
    void loadConversationList()
    preloadAssistantTools()
  }
)
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  if (props.visible) {
    void loadConversationList()
    preloadAssistantTools()
  }
})
onUnmounted(() => document.removeEventListener('keydown', handleKeydown))

return { __sfc: true,props, emits, sessions, activeSessionId, activeGroup, historyQuery, historyCollapsed, timelineRef, composerRef, groups, assistantToolsLoader, activeSession, conversationListError, conversationListLoaded, busy, filteredSessions, scrollToBottom, newSession, preloadAssistantTools, loadConversationList, selectSession, confirmDeleteSession, deleteSession, commitSession, send, updateOrderImportState, importOrderSelections, updateProductionHistoryState, exportProductionHistorySelections, changeProductionHistoryPage, handleClose, handleKeydown, changeOrderImportPage, ASSISTANT_PANEL_Z_INDEX, GAssistantComposer, GAssistantTimeline }
}

})